/**
 * RS Customizer — Icon Tote
 * Visual preview renderer for icon tote customization.
 * Integrates with the charm builder slot system: reads filled slots,
 * fetches icon artwork via Storefront API GraphQL, and renders icon images
 * or custom text at metaobject-defined slot positions on the gallery preview slide.
 *
 * Charm selection UI is handled by rs-product-charm-builder.js.
 * MTO input validation / ATC gating is handled by rs-product-mto-input.js / rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const LIQUID_VIEW = 'metaobject-tote-icon';

  const ICON_LIBRARY_QUERY = `
    query GetAllToteIcons($first: Int!) {
      metaobjects(type: "customizer_tote_icon", first: $first) {
        edges {
          node {
            handle
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image { url altText }
                }
              }
            }
          }
        }
      }
    }`;

  const FONT_BASE_SIZES = { block: 58, script: 72 };
  const SCRIPT_MULTIPLIER = 1.4;
  const MAX_FONT_RATIO = 0.08;
  const BASE_CONTAINER_WIDTH = 1600;
  const PT_TO_PX = 1.33;
  const DEFAULT_ICON_WIDTH = 23;
  const DEFAULT_MAX_WIDTH_FALLBACK = 25;

  // ─── State ──────────────────────────────────────────────────────

  let _injectedSlideId = null;
  let _metaobjectData = null;
  let _backgroundsData = null;
  let _currentToteSize = null;
  let _selectedColor = null;
  let _slotsObserver = null;
  let _resizeTimer = null;
  let _charmConfig = null;
  let _iconLibrary = null;
  let _iconLibraryPromise = null;

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      `${window.RSProduct.config.productUrl}?view=${LIQUID_VIEW}`
    );
    return response.json();
  }

  async function _fetchIconLibrary() {
    if (_iconLibrary) return _iconLibrary;
    if (_iconLibraryPromise) return _iconLibraryPromise;

    if (!window.RSGraphQL) {
      console.warn('[ToteIcon] RSGraphQL not available');
      return [];
    }

    _iconLibraryPromise = window.RSGraphQL
      .executeQuery(ICON_LIBRARY_QUERY, { first: 250 })
      .then((data) => {
        const icons = [];
        if (data?.metaobjects?.edges) {
          data.metaobjects.edges.forEach((edge) => {
            const parsed = _parseIconFields(edge.node.handle, edge.node.fields);
            if (parsed) icons.push(parsed);
          });
        }
        _iconLibrary = icons;
        return icons;
      })
      .catch((error) => {
        console.error('[ToteIcon] Icon library fetch failed:', error);
        _iconLibrary = [];
        return [];
      });

    return _iconLibraryPromise;
  }

  function _parseIconFields(handle, fields) {
    const result = { handle, name: handle, image: null };
    if (!fields) return null;

    fields.forEach((field) => {
      if (field.reference?.image) {
        result[field.key] = field.reference.image.url;
        if (field.key === 'image') result.image = field.reference.image.url;
      } else if (field.value != null) {
        result[field.key] = field.value;
        if (field.key === 'variant_title') result.name = field.value;
      }
    });

    return result.image ? result : null;
  }

  function _findIconByTitle(title) {
    if (!_iconLibrary || !title) return null;
    const searchTerm = title.toLowerCase().trim();

    const exactMatch = _iconLibrary.find((icon) => {
      const variantTitle = (icon.variant_title || '').toLowerCase().trim();
      return variantTitle === searchTerm;
    });
    if (exactMatch) return exactMatch;

    const handleMatch = _iconLibrary.find(
      (icon) => icon.handle.toLowerCase() === searchTerm
    );
    if (handleMatch) return handleMatch;

    const withoutIconSuffix = searchTerm
      .replace(/\s*icon\s*$/i, '')
      .replace(/^icon\s*/i, '')
      .trim();
    if (withoutIconSuffix !== searchTerm) {
      const partialMatch = _iconLibrary.find((icon) => {
        const variantTitle = (icon.variant_title || '').toLowerCase().trim();
        return variantTitle === withoutIconSuffix || icon.handle.toLowerCase() === withoutIconSuffix;
      });
      if (partialMatch) return partialMatch;
    }

    return null;
  }

  // ─── Metaobject accessors ───────────────────────────────────────

  function _getBackgroundForSize(toteSize) {
    if (!_backgroundsData || !toteSize) return null;
    return _backgroundsData[toteSize] || null;
  }

  function _getToteSizeForVariant(variantTitle) {
    if (!_metaobjectData || !variantTitle) return null;

    const productTitle = _metaobjectData.product_title || '';
    const variantKey = `${productTitle} - ${variantTitle}`;
    const variantData = _metaobjectData.customizer_data
      ? _metaobjectData.customizer_data[variantKey]
      : null;

    return variantData ? variantData.tote_size : null;
  }

  function _getSettingsForSize(toteSize) {
    if (!_metaobjectData?.settings || !toteSize) return null;
    return _metaobjectData.settings[toteSize] || null;
  }

  function _getSlotPosition(slotIndex) {
    const settings = _getSettingsForSize(_currentToteSize);
    if (!settings?.slot_positions) return null;
    return settings.slot_positions[slotIndex] || null;
  }

  function _getMaxSlots() {
    const settings = _getSettingsForSize(_currentToteSize);
    return settings ? (settings.max_number_icons || 0) : 0;
  }

  function _getFontScalingMultiplier() {
    const settings = _getSettingsForSize(_currentToteSize);
    return settings ? (settings.font_scaling_multiplier || 1.0) : 1.0;
  }

  // ─── Gallery content container ──────────────────────────────────

  function _getContentContainer() {
    if (!_injectedSlideId) return null;

    const slide = document.querySelector(
      `[data-rs-injected="${_injectedSlideId}"]`
    );
    if (!slide) return null;

    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;

    let canvas = customizerView.querySelector('.rs-tote-icon__canvas');
    if (!canvas) {
      canvas = document.createElement('div');
      canvas.className = 'rs-tote-icon__canvas';
      customizerView.appendChild(canvas);
    }

    return canvas;
  }

  // ─── Color management ──────────────────────────────────────────

  function _resolveColorHex(colorKey) {
    if (!colorKey || !_metaobjectData?.color_names) return '#000000';
    if (/^#[0-9A-Fa-f]{6}$/.test(colorKey)) return colorKey;

    const match = _metaobjectData.color_names.find(
      (colorEntry) =>
        colorEntry.key === colorKey ||
        colorEntry.key.toLowerCase() === colorKey.toLowerCase() ||
        colorEntry.name === colorKey ||
        colorEntry.name.toLowerCase() === colorKey.toLowerCase()
    );

    return match ? match.color : '#000000';
  }

  function _applyColorCssVars(hex) {
    if (!hex) return;
    const red = parseInt(hex.slice(1, 3), 16);
    const green = parseInt(hex.slice(3, 5), 16);
    const blue = parseInt(hex.slice(5, 7), 16);
    const root = document.documentElement;
    root.style.setProperty('--customizer-tote-icon-color', hex);
    root.style.setProperty('--customizer-tote-icon-r', red);
    root.style.setProperty('--customizer-tote-icon-g', green);
    root.style.setProperty('--customizer-tote-icon-b', blue);
  }

  // ─── Charm builder integration ─────────────────────────────────

  function _getCharmBuilder() {
    return document.querySelector('rs-product-charm-builder');
  }

  function _readSlots() {
    const builder = _getCharmBuilder();
    if (!builder?.slots) return [];
    return builder.slots;
  }

  function _loadCharmConfig() {
    if (_charmConfig) return _charmConfig;
    const configElement = document.getElementById(
      `rs-charm-config-${window.RSProduct.sectionId}`
    );
    if (!configElement) return null;
    try {
      _charmConfig = JSON.parse(configElement.textContent);
      return _charmConfig;
    } catch (error) {
      return null;
    }
  }

  function _isTextCategory(categoryIndex) {
    const config = _loadCharmConfig();
    if (!config?.categories || categoryIndex === undefined) return false;
    const category = config.categories[categoryIndex];
    if (!category) return false;
    const title = (category.title || '').toLowerCase();
    return title.includes('font') || title.includes('text');
  }

  function _isScriptFont(categoryIndex, subIndex) {
    const config = _loadCharmConfig();
    if (!config?.categories || categoryIndex === undefined) return false;
    const category = config.categories[categoryIndex];
    if (!category) return false;
    if (category.subcategories?.length && subIndex !== undefined) {
      const sub = category.subcategories[subIndex];
      if (sub) return (sub.title || '').toLowerCase().includes('script');
    }
    return (category.title || '').toLowerCase().includes('script');
  }

  function _getColorForSlot(slot) {
    if (!slot) return '#000000';
    if (slot.itemTitle) return _resolveColorHex(slot.itemTitle);
    if (slot.title) return _resolveColorHex(slot.title);
    return '#000000';
  }

  // ─── Font scaling ──────────────────────────────────────────────

  function _calculateFontRatio(fontType, textLength, positionData) {
    const basePointSize = FONT_BASE_SIZES[fontType] || FONT_BASE_SIZES.block;
    const sizeInPixels = basePointSize * PT_TO_PX;
    let fontRatio = sizeInPixels / BASE_CONTAINER_WIDTH;

    const scalingAdjustment = 0.6;
    const sizeMultiplier = _getFontScalingMultiplier();
    fontRatio *= scalingAdjustment * sizeMultiplier;

    if (positionData) {
      const hasMaxWidth = positionData.max_width && positionData.max_width > 0;
      const hasMaxHeight = positionData.max_height && positionData.max_height > 0;

      if (hasMaxWidth) {
        const constraintFactor = Math.min(positionData.max_width / 100, 1.0);
        fontRatio *= constraintFactor * (positionData.max_width < 30 ? 0.9 : 1.0);
      } else if (hasMaxHeight) {
        const widthConstraint = DEFAULT_ICON_WIDTH;
        const factor = Math.min(widthConstraint / 100, 1.0);
        fontRatio *= factor * 0.9;
      }
    }

    if (fontType === 'script') {
      fontRatio *= SCRIPT_MULTIPLIER;
    }

    if (textLength >= 1 && textLength <= 6) {
      fontRatio *= 1.2;
    } else if (textLength >= 7) {
      fontRatio *= 0.8;
    }

    return Math.min(fontRatio, MAX_FONT_RATIO);
  }

  // ─── Rendering ─────────────────────────────────────────────────

  function _clearSlotItems() {
    const container = _getContentContainer();
    if (!container) return;
    container.querySelectorAll('[data-tote-slot]').forEach((element) => element.remove());
  }

  function _renderAllSlots() {
    const container = _getContentContainer();
    if (!container) return;

    _clearSlotItems();

    const slots = _readSlots();
    const maxSlots = _getMaxSlots();

    for (let index = 0; index < Math.min(slots.length, maxSlots); index++) {
      const slot = slots[index];
      if (!slot) continue;

      const position = _getSlotPosition(index);
      if (!position) continue;

      const hasText = slot.customization && slot.customization.trim();
      const isText = _isTextCategory(slot._catIdx);

      if (isText && hasText) {
        const isScript = _isScriptFont(slot._catIdx, slot._subIdx);
        const fontType = isScript ? 'script' : 'block';
        const color = _getColorForSlot(slot);
        _renderTextAtPosition(container, slot.customization.trim(), fontType, color, position, index);
      } else if (!isText) {
        _renderIconForSlot(container, slot, position, index);
      }
    }

    _renderOverlay();
  }

  function _renderIconForSlot(container, slot, position, slotIndex) {
    const displayTitle = slot.itemTitle || slot.title || '';
    const iconMatch = _findIconByTitle(displayTitle);
    const imageUrl = iconMatch ? iconMatch.image : slot.imageUrl;
    if (!imageUrl) return;

    _renderIconAtPosition(container, imageUrl, position, slotIndex);
  }

  function _renderIconAtPosition(container, imageUrl, position, slotIndex) {
    const scalingFactor = position.scaling_factor || 1.0;
    const clampedMaxWidth = position.max_width > 50 ? 50 : position.max_width;
    const hasMaxWidth = clampedMaxWidth && clampedMaxWidth > 0;
    const hasMaxHeight = position.max_height && position.max_height > 0;

    let maxWidth, maxHeight;
    if (hasMaxWidth && hasMaxHeight) {
      maxWidth = 'auto';
      maxHeight = position.max_height;
    } else if (hasMaxWidth) {
      maxWidth = clampedMaxWidth;
      maxHeight = 'auto';
    } else if (hasMaxHeight) {
      maxWidth = 'auto';
      maxHeight = position.max_height;
    } else {
      maxWidth = DEFAULT_MAX_WIDTH_FALLBACK;
      maxHeight = 'auto';
    }

    const widthCSS = maxWidth === 'auto' ? 'auto' : `${maxWidth * scalingFactor}%`;
    const heightCSS = maxHeight === 'auto' ? 'auto' : `${maxHeight * scalingFactor}%`;

    const wrapper = document.createElement('div');
    wrapper.className = 'rs-tote-icon__item';
    wrapper.setAttribute('data-tote-slot', slotIndex);
    wrapper.style.cssText = `
      position: absolute;
      left: ${position.x_position}%;
      top: ${position.y_position}%;
      width: ${widthCSS};
      height: ${heightCSS};
      transform: translate(-50%, -50%);
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    `;

    const img = document.createElement('img');
    img.className = 'rs-tote-icon__icon';
    img.src = imageUrl;
    img.alt = 'Tote Icon';

    wrapper.appendChild(img);
    container.appendChild(wrapper);
  }

  function _renderTextAtPosition(container, text, fontType, color, position, slotIndex) {
    const containerWidth = container.offsetWidth || 800;
    const fontRatio = _calculateFontRatio(fontType, text.length, position);
    const fontClass = fontType === 'script'
      ? 'rs-tote-icon__text rs-tote-icon__text--script'
      : 'rs-tote-icon__text rs-tote-icon__text--block';

    const hasMaxWidth = position.max_width && position.max_width > 0;
    const hasMaxHeight = position.max_height && position.max_height > 0;
    const maxWidthConstraint = hasMaxWidth
      ? `${position.max_width}%`
      : `${DEFAULT_ICON_WIDTH}%`;

    const element = document.createElement('div');
    element.className = fontClass;
    element.setAttribute('data-tote-slot', slotIndex);
    element.setAttribute('data-font-ratio', fontRatio);
    element.textContent = text;

    element.style.setProperty('--container-width', `${containerWidth}px`);
    element.style.setProperty('--font-size-ratio', fontRatio);
    element.style.setProperty('--max-width-constraint', maxWidthConstraint);
    element.style.left = `${position.x_position}%`;
    element.style.top = `${position.y_position}%`;
    element.style.color = color;

    if (hasMaxHeight) {
      element.style.maxHeight = `${position.max_height}%`;
    }

    container.appendChild(element);
  }

  // ─── Overlay rendering ────────────────────────────────────────

  function _renderOverlay() {
    const container = _getContentContainer();
    if (!container) return;

    const existing = container.querySelector('.rs-tote-icon__overlay');
    if (existing) existing.remove();

    if (!_currentToteSize || !_metaobjectData?.overlays) return;
    const overlayUrl = _metaobjectData.overlays[_currentToteSize];
    if (!overlayUrl) return;

    const overlay = document.createElement('div');
    overlay.className = 'rs-tote-icon__overlay';

    const img = document.createElement('img');
    img.src = overlayUrl;
    img.alt = 'Customizer Overlay';
    overlay.appendChild(img);

    container.appendChild(overlay);
  }

  // ─── Resize handling ──────────────────────────────────────────

  function _handleResize() {
    const container = _getContentContainer();
    if (!container) return;
    const containerWidth = container.offsetWidth || 800;

    container.querySelectorAll('.rs-tote-icon__text').forEach((element) => {
      element.style.setProperty('--container-width', `${containerWidth}px`);
    });
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(_handleResize, 250);
  }

  // ─── Charm slots observer ─────────────────────────────────────

  function _setupSlotsObserver() {
    const slotsElement = document.querySelector('.rs-charm__slots');
    if (!slotsElement) return;

    if (_slotsObserver) _slotsObserver.disconnect();

    _slotsObserver = new MutationObserver(() => {
      _renderAllSlots();
    });

    _slotsObserver.observe(slotsElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function _waitForSlotsElement() {
    const slotsElement = document.querySelector('.rs-charm__slots');
    if (slotsElement) {
      _setupSlotsObserver();
      _renderAllSlots();
      return;
    }

    const bodyObserver = new MutationObserver((mutations, observer) => {
      const element = document.querySelector('.rs-charm__slots');
      if (element) {
        observer.disconnect();
        _setupSlotsObserver();
        _renderAllSlots();
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ─── Event handlers ──────────────────────────────────────────

  function _navigateToSlide() {
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleCharmInteraction(event) {
    if (event.target.closest('.rs-charm__grid-item')) {
      _navigateToSlide();
    }
  }

  function _handleInputFocus(event) {
    if (event.target.matches?.('[data-text-input]')) {
      _navigateToSlide();
    }
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;

    const variant = event.data.variant;
    if (!variant?.title) return;

    const newToteSize = _getToteSizeForVariant(variant.title);
    _currentToteSize = newToteSize;

    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForSize(_currentToteSize);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    _renderAllSlots();

    const slots = _readSlots();
    const hasContent = slots.some((slot) => slot !== null);
    if (hasContent && _injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleColorRadioChange(event) {
    const input = event.target;
    if (
      !input.matches ||
      !input.matches('input[type="radio"]') ||
      !_metaobjectData?.color_names
    ) return;

    const sectionElement = document.getElementById(
      `MainProduct--${window.RSProduct.sectionId}`
    );
    if (!sectionElement || !sectionElement.contains(input)) return;

    const colorKey = (input.value || '').trim();
    if (!colorKey) return;

    const hex = _resolveColorHex(colorKey);
    if (hex && hex !== '#000000') {
      _selectedColor = hex;
      _applyColorCssVars(_selectedColor);
    }
  }

  // ─── Initialization ───────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig;
    try {
      [baseConfig] = await Promise.all([_fetchBaseConfig(), _fetchIconLibrary()]);
    } catch (error) {
      console.error('[ToteIcon] Data fetch failed:', error);
      return;
    }

    _metaobjectData = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};

    const initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    const variantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;
    _currentToteSize = _getToteSizeForVariant(variantTitle);

    const backgroundUrl = _getBackgroundForSize(_currentToteSize);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    _renderOverlay();
    _waitForSlotsElement();

    document.addEventListener('change', _handleColorRadioChange);
    document.addEventListener('click', _handleCharmInteraction);
    document.addEventListener('focusin', _handleInputFocus);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
      window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        _handleVariantChange
      );
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (!window.RSProduct) return;
    _init();
  });
})();
